import { useMemo, useState } from 'react';
import { useApiQuery } from '@halo/web-lib';
import { Briefcase, ExternalLink, RefreshCw } from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

type JobExecutionState =
  | 'EXECUTION_STATE_UNSPECIFIED'
  | 'CONDITION_PENDING'
  | 'CONDITION_RUNNING'
  | 'CONDITION_SUCCEEDED'
  | 'CONDITION_FAILED'
  | 'CONDITION_CANCELLED';

interface JobExecutionSummary {
  executionName: string;
  jobName: string;
  startTime: string | null;
  completionTime: string | null;
  state: JobExecutionState;
  succeededCount: number;
  failedCount: number;
  retriedCount: number;
  logUri: string | null;
}

interface ExecutionsResponse {
  executions: JobExecutionSummary[];
}

interface JobOption {
  jobName: string;
  label: string;
}

const JOB_OPTIONS: readonly JobOption[] = [
  { jobName: 'halo-ingestion-backfill', label: 'halo-ingestion-backfill' },
] as const;

/* ─── Helpers ─────────────────────────────────── */

function formatState(state: JobExecutionState): string {
  return state.replace(/^CONDITION_/, '').replace(/^EXECUTION_STATE_/, '').toLowerCase();
}

function stateBadgeClasses(state: JobExecutionState): string {
  switch (state) {
    case 'CONDITION_SUCCEEDED':
      return 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400';
    case 'CONDITION_RUNNING':
      return 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400';
    case 'CONDITION_FAILED':
      return 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400';
    default:
      return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  }
}

function stateDotClasses(state: JobExecutionState): string {
  switch (state) {
    case 'CONDITION_SUCCEEDED':
      return 'bg-green-500';
    case 'CONDITION_RUNNING':
      return 'bg-blue-500 animate-pulse';
    case 'CONDITION_FAILED':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
}

function StateBadge({ state }: { state: JobExecutionState }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded ${stateBadgeClasses(state)}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${stateDotClasses(state)}`} />
      {formatState(state)}
    </span>
  );
}

function humanizeDuration(startIso: string | null, endIso: string | null): string {
  if (!startIso) return '—';
  const startMs = Date.parse(startIso);
  if (Number.isNaN(startMs)) return '—';
  const endMs = endIso ? Date.parse(endIso) : Date.now();
  if (Number.isNaN(endMs)) return '—';
  const diffSec = Math.max(0, Math.floor((endMs - startMs) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const minutes = Math.floor(diffSec / 60);
  const seconds = diffSec % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return `${hours}h ${remMinutes}m`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleString();
}

function shortenExecutionName(executionName: string): string {
  const parts = executionName.split('/');
  return parts[parts.length - 1] || executionName;
}

/* ─── Main Component ──────────────────────────── */

export function CloudRunJobsPage() {
  const [selectedJob, setSelectedJob] = useState<string>(JOB_OPTIONS[0]?.jobName ?? 'halo-ingestion-backfill');

  const queryKey = useMemo(
    () => ['admin', 'cloud-run-jobs', selectedJob] as const,
    [selectedJob],
  );

  const { data, isLoading, isFetching, refetch, error } = useApiQuery<ExecutionsResponse>(
    queryKey,
    `/api/v1/ingestion/admin/cloud-run/jobs/${encodeURIComponent(selectedJob)}/executions`,
    {
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  const executions = data?.executions ?? [];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 p-2 rounded-lg bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
            <Briefcase className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Cloud Run Jobs</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Recent execution history for heavy-backfill Cloud Run Jobs.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Job
          </label>
          <select
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200"
          >
            {JOB_OPTIONS.map((opt) => (
              <option key={opt.jobName} value={opt.jobName}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          Failed to load executions: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : executions.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <Briefcase className="w-10 h-10 text-gray-400 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">No executions yet.</div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Heavy backfills trigger this job automatically.
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="text-left px-4 py-2 font-medium">Execution</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-left px-4 py-2 font-medium">Start time</th>
                <th className="text-left px-4 py-2 font-medium">Duration</th>
                <th className="text-right px-4 py-2 font-medium">Succeeded</th>
                <th className="text-right px-4 py-2 font-medium">Failed</th>
                <th className="text-right px-4 py-2 font-medium">Retried</th>
                <th className="text-right px-4 py-2 font-medium">Logs</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((exec) => (
                <tr
                  key={exec.executionName}
                  className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-950/50"
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-700 dark:text-gray-200">
                    {shortenExecutionName(exec.executionName)}
                  </td>
                  <td className="px-4 py-2.5">
                    <StateBadge state={exec.state} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">
                    {formatTimestamp(exec.startTime)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">
                    {humanizeDuration(exec.startTime, exec.completionTime)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-green-700 dark:text-green-400">
                    {exec.succeededCount}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-red-700 dark:text-red-400">
                    {exec.failedCount}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-gray-600 dark:text-gray-400">
                    {exec.retriedCount}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {exec.logUri ? (
                      <a
                        href={exec.logUri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Open
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info panel */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          Heavy backfills (≥6 months) auto-route to this job. Trigger via Ingestion → Backfill UI.
        </div>
      </div>
    </div>
  );
}
